import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FlaskConical, Plus, Trash2, Trophy, BarChart3, TrendingUp, Eye,
  MousePointer, Heart, MessageCircle, Share2, Sparkles, ChevronDown,
  ChevronUp, Target, Copy, ArrowRight, Loader2
} from 'lucide-react';
import { api, isUpgradeError } from '../context/AuthContext';
import { CreditCost } from '../components/CreditMeter';
import { toast } from 'sonner';

const METRIC_LABELS = {
  engagement_rate: { label: 'Engagement Rate', unit: '%' },
  clicks: { label: 'Clicks', unit: '' },
  reach: { label: 'Reach', unit: '' },
  engagement: { label: 'Engagement', unit: '' },
};

const PLATFORM_COLORS = {
  facebook: 'bg-blue-600', instagram: 'bg-pink-500', twitter: 'bg-sky-500',
  linkedin: 'bg-blue-700', tiktok: 'bg-slate-800', youtube: 'bg-red-600',
};

// ========== Variant Comparison Bar ==========
const VariantBar = ({ variant, maxValue, isWinner, metricKey }) => {
  const value = variant.metrics?.[metricKey] || 0;
  const width = maxValue > 0 ? (value / maxValue) * 100 : 0;

  return (
    <div className={`p-3 rounded-xl border transition-all ${isWinner ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 bg-white'}`} data-testid={`variant-${variant.letter}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${isWinner ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
            {variant.letter}
          </span>
          <span className="text-sm font-medium text-slate-800">{variant.name}</span>
          {isWinner && <Badge className="bg-emerald-500 text-white text-[10px]"><Trophy className="h-3 w-3 mr-0.5" />Winner</Badge>}
        </div>
        <span className={`text-lg font-bold ${isWinner ? 'text-emerald-600' : 'text-slate-700'}`}>
          {value}{METRIC_LABELS[metricKey]?.unit}
        </span>
      </div>
      <div className="w-full bg-slate-100 rounded-full h-2.5">
        <div className={`h-2.5 rounded-full transition-all ${isWinner ? 'bg-emerald-500' : 'bg-slate-400'}`} style={{ width: `${width}%` }} />
      </div>
      <p className="text-xs text-slate-500 mt-2 line-clamp-2">{variant.content}</p>
      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
        <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{variant.metrics?.reach || 0}</span>
        <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{variant.metrics?.likes || 0}</span>
        <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" />{variant.metrics?.comments || 0}</span>
        <span className="flex items-center gap-1"><Share2 className="h-3 w-3" />{variant.metrics?.shares || 0}</span>
        <span className="flex items-center gap-1"><MousePointer className="h-3 w-3" />{variant.metrics?.clicks || 0}</span>
      </div>
    </div>
  );
};

// ========== Test Result Card ==========
const TestResultCard = ({ test, onDelete, onAnalyze }) => {
  const [expanded, setExpanded] = useState(false);
  const [analysis, setAnalysis] = useState(test.ai_analysis || null);
  const [analyzing, setAnalyzing] = useState(false);

  const metricKey = test.success_metric || 'engagement_rate';
  const maxValue = Math.max(...(test.variants || []).map(v => v.metrics?.[metricKey] || 0), 1);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post(`/ab-tests/${test.test_id}/analyze`);
      setAnalysis(res.data.analysis);
    } catch (e) {
      if (!isUpgradeError(e)) toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <Card className="overflow-hidden" data-testid="ab-test-card">
      <CardContent className="p-0">
        <div className="p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-slate-800">{test.name}</h3>
                <Badge variant="outline" className={`text-[10px] ${test.status === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : test.status === 'running' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-slate-50 text-slate-600'}`}>
                  {test.status}
                </Badge>
                <Badge variant="outline" className="text-[10px]">{METRIC_LABELS[metricKey]?.label || metricKey}</Badge>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                <span>{test.variants?.length || 0} variants</span>
                <span>{(test.platforms || []).join(', ')}</span>
                <span>{new Date(test.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </div>
          </div>
        </div>

        {expanded && (
          <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
            {(test.variants || []).map(v => (
              <VariantBar
                key={v.variant_id}
                variant={v}
                maxValue={maxValue}
                isWinner={v.variant_id === test.winner_variant_id}
                metricKey={metricKey}
              />
            ))}

            <div className="flex items-center gap-2 pt-2">
              <Button size="sm" variant="outline" className="text-xs" onClick={(e) => { e.stopPropagation(); handleAnalyze(); }} disabled={analyzing} data-testid="analyze-test-btn">
                <Sparkles className="h-3 w-3 mr-1" />{analyzing ? 'Analyzing...' : 'AI Analysis'}<CreditCost feature="ab_analysis" />
              </Button>
              <Button size="sm" variant="ghost" className="text-xs text-red-500" onClick={(e) => { e.stopPropagation(); onDelete(test.test_id); }} data-testid="delete-test-btn">
                <Trash2 className="h-3 w-3 mr-1" />Delete
              </Button>
            </div>

            {analysis && (
              <div className="p-3 bg-violet-50 rounded-xl border border-violet-200 mt-2" data-testid="ai-analysis-result">
                <p className="text-xs font-medium text-violet-700 mb-1 flex items-center gap-1"><Sparkles className="h-3 w-3" />AI Analysis</p>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{analysis}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ========== Create A/B Test Dialog ==========
const CreateTestDialog = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [platforms, setPlatforms] = useState(['facebook']);
  const [successMetric, setSuccessMetric] = useState('engagement_rate');
  const [durationHours, setDurationHours] = useState(24);
  const [variants, setVariants] = useState([
    { name: 'Variant A', content: '', hashtags: [], cta: '' },
    { name: 'Variant B', content: '', hashtags: [], cta: '' },
  ]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);

  const updateVariant = (idx, field, value) => setVariants(prev => prev.map((v, i) => i === idx ? { ...v, [field]: value } : v));
  const addVariant = () => {
    if (variants.length >= 5) { toast.error('Maximum 5 variants'); return; }
    setVariants(prev => [...prev, { name: `Variant ${String.fromCharCode(65 + prev.length)}`, content: '', hashtags: [], cta: '' }]);
  };
  const removeVariant = (idx) => {
    if (variants.length <= 2) { toast.error('Minimum 2 variants required'); return; }
    setVariants(prev => prev.filter((_, i) => i !== idx));
  };

  const handleGenerate = async () => {
    const baseContent = variants[0]?.content;
    if (!baseContent?.trim()) { toast.error('Enter content in Variant A first'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/ab-tests/generate-variants', null, {
        params: { content: baseContent, variant_count: variants.length, platforms }
      });
      const generated = res.data.variants || [];
      setVariants(prev => prev.map((v, i) => ({
        ...v,
        name: generated[i]?.name || v.name,
        content: generated[i]?.content || v.content,
      })));
      toast.success(`${generated.length} variants generated (${res.data.source})`);
    } catch (e) {
      if (!isUpgradeError(e)) toast.error('Failed to generate variants');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Name required'); return; }
    if (variants.some(v => !v.content.trim())) { toast.error('All variants need content'); return; }
    setLoading(true);
    try {
      const res = await api.post('/ab-tests/', {
        name, description, platforms, success_metric: successMetric,
        duration_hours: durationHours,
        variants: variants.map(v => ({
          name: v.name, content: v.content,
          hashtags: v.hashtags || [], cta: v.cta || null, media_urls: [],
        })),
      });
      toast.success(`A/B test "${res.data.name}" created`);
      onCreated(res.data);
      setName(''); setDescription('');
      setVariants([
        { name: 'Variant A', content: '', hashtags: [], cta: '' },
        { name: 'Variant B', content: '', hashtags: [], cta: '' },
      ]);
      onClose();
    } catch (e) {
      if (!isUpgradeError(e)) toast.error(e.response?.data?.detail || 'Failed to create test');
    } finally {
      setLoading(false);
    }
  };

  const togglePlatform = (p) => setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-test-dialog">
        <DialogHeader>
          <DialogTitle>Create A/B Test</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Test Name</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., CTA Style Test" className="mt-1" data-testid="test-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Success Metric</label>
              <Select value={successMetric} onValueChange={setSuccessMetric}>
                <SelectTrigger className="mt-1" data-testid="metric-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(METRIC_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Platforms</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PLATFORM_COLORS).map(([key, color]) => (
                <button key={key} onClick={() => togglePlatform(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${platforms.includes(key) ? `${color} text-white` : 'bg-slate-100 text-slate-500'}`}
                >{key.charAt(0).toUpperCase() + key.slice(1)}</button>
              ))}
            </div>
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Variants ({variants.length})</label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={handleGenerate} disabled={generating} data-testid="generate-variants-btn">
                  {generating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                  AI Generate<CreditCost feature="ab_variants" />
                </Button>
                <Button size="sm" variant="ghost" className="text-xs h-7" onClick={addVariant}>
                  <Plus className="h-3 w-3 mr-1" />Add Variant
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {variants.map((v, i) => (
                <div key={i} className="p-3 border border-slate-200 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-md bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">{String.fromCharCode(65 + i)}</span>
                      <Input value={v.name} onChange={e => updateVariant(i, 'name', e.target.value)} className="h-7 text-sm w-40" data-testid={`variant-name-${i}`} />
                    </div>
                    {variants.length > 2 && (
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400" onClick={() => removeVariant(i)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <Textarea value={v.content} onChange={e => updateVariant(i, 'content', e.target.value)}
                    placeholder={`Write variant ${String.fromCharCode(65 + i)} content...`}
                    className="text-sm min-h-[60px]" data-testid={`variant-content-${i}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={loading} data-testid="create-test-btn">
            {loading ? 'Creating...' : 'Create & Run Test'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ========== Main Page ==========
export default function ABTestingPage() {
  const [tests, setTests] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [testsRes, statsRes] = await Promise.all([
        api.get('/ab-tests/'),
        api.get('/ab-tests/stats/summary'),
      ]);
      setTests(testsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (testId) => {
    if (!window.confirm('Delete this A/B test?')) return;
    try {
      await api.delete(`/ab-tests/${testId}`);
      setTests(prev => prev.filter(t => t.test_id !== testId));
      toast.success('Test deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="ab-testing-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">A/B Testing</h1>
            <p className="text-sm text-slate-500 mt-1">Compare post variants to optimize engagement</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="add-test-btn">
            <Plus className="h-4 w-4 mr-2" />New A/B Test
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">Total Tests</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_tests}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">Completed</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{stats.completed}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">Running</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{stats.running}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">Avg. Improvement</p>
              <p className="text-2xl font-bold text-violet-600 mt-1">{stats.avg_improvement_pct}%</p>
            </CardContent></Card>
          </div>
        )}

        {/* Tests List */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading tests...</div>
        ) : tests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FlaskConical className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-3">No A/B tests yet</p>
              <p className="text-xs text-slate-400 mb-4">Create your first test to compare different post versions</p>
              <Button onClick={() => setShowCreateDialog(true)} data-testid="empty-add-test-btn">
                <Plus className="h-4 w-4 mr-2" />Create First Test
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="tests-list">
            {tests.map(test => (
              <TestResultCard key={test.test_id} test={test} onDelete={handleDelete} />
            ))}
          </div>
        )}

        <CreateTestDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} onCreated={(t) => { setTests(prev => [t, ...prev]); fetchData(); }} />
      </div>
    </DashboardLayout>
  );
}
